import { useCallback } from 'react';
import { CalDateInfo, SlotInfo } from '../../../types/schedule.types';
import { SceneInfo } from '../../../types/scene.types';
import {
  computeSlotLocationVisibilityDedupePatches,
  computeSlotLocationVisibilityDedupePatchesForDay,
} from '../../../utils/calDateSlotsSortUtils';
import { BatchSlotLocationVisibilityItem } from '../../../api/schedule';

interface UseSlotLocationOrganizeParams {
  scheduleID: string | undefined;
  optimisticCalDates: CalDateInfo[];
  optimisticSlots: SlotInfo[];
  getSceneInfo: (sceneID: string | undefined) => SceneInfo | undefined;
  batchPatchSlotLocationVisibilityData: (
    scheduleID: string,
    updates: BatchSlotLocationVisibilityItem[],
  ) => Promise<void>;
  t: (key: string) => string;
}

export function useSlotLocationOrganize({
  scheduleID,
  optimisticCalDates,
  optimisticSlots,
  getSceneInfo,
  batchPatchSlotLocationVisibilityData,
  t,
}: UseSlotLocationOrganizeParams) {
  const normalizeLocationVisibilityForDates = useCallback(
    async (dateStrs: string[]) => {
      if (!scheduleID) return;
      const uniq = Array.from(new Set(dateStrs.filter(Boolean)));
      if (uniq.length === 0) return;
      const items: BatchSlotLocationVisibilityItem[] = [];
      for (let i = 0; i < uniq.length; i++) {
        const dateStr = uniq[i];
        const pd = optimisticCalDates.find((d) => d.calDateDt?.split('T')[0] === dateStr);
        const patches = computeSlotLocationVisibilityDedupePatchesForDay(
          scheduleID,
          dateStr,
          pd,
          optimisticSlots,
          (sceneId) => (getSceneInfo(sceneId)?.sceneLocation ?? '').trim(),
        );
        if (patches.length > 0) items.push(...patches);
      }
      if (items.length === 0) return;
      await batchPatchSlotLocationVisibilityData(scheduleID, items);
    },
    [
      scheduleID,
      optimisticCalDates,
      optimisticSlots,
      getSceneInfo,
      batchPatchSlotLocationVisibilityData,
    ],
  );

  const handleGroupByLocation = useCallback(async () => {
    if (!scheduleID) return;
    try {
      const patches = computeSlotLocationVisibilityDedupePatches(
        scheduleID,
        optimisticCalDates,
        optimisticSlots,
        (sceneId) => (getSceneInfo(sceneId)?.sceneLocation ?? '').trim(),
      );
      if (patches.length === 0) {
        console.warn('[Schedule] 按地点整理：没有需要写入的变更。', {
          calDates: optimisticCalDates.length,
          slots: optimisticSlots.length,
        });
        return;
      }
      await batchPatchSlotLocationVisibilityData(scheduleID, patches);
      console.info('[Schedule] 按地点整理：已提交', patches.length, '条 slot 地点头显示更新');
    } catch (err) {
      console.error('按地点整理失败:', err);
      alert(
        t('schedule.organizeByLocationFailed') +
          (err instanceof Error ? err.message : t('common.unknownError')),
      );
    }
  }, [
    scheduleID,
    optimisticCalDates,
    optimisticSlots,
    getSceneInfo,
    batchPatchSlotLocationVisibilityData,
    t,
  ]);

  return { handleGroupByLocation, normalizeLocationVisibilityForDates };
}

